import { Flex, Heading } from '@chakra-ui/react';
import { useActivityInformationHotelQuery } from '../api/useActivityInformationHotelQuery';
import { HotelInfoCard } from '../shared/HotelInfoCard';
import { BaseSkeletonStack, BaseCard } from '@cks-internal/react-base';
import { FooterFlsqBadge } from '../shared/FooterFlsqBadge';

type Props = {
  hotelIds: number[];
  flsq?: number;
};
/**
 * ActivityInformationHotelDisplay component fetches and renders a list of hotels for the activity.
 *
 * Responsibilities:
 * - Queries hotel data using `useActivityInformationHotelQuery` with provided hotel IDs.
 * - Refetches whenever `props.hotelIds` changes.
 * - Displays a loading skeleton while data is loading.
 * - Renders nothing if no data is returned.
 * - Once data is available, maps over each hotel value and renders a <HotelInfoCard>.
 *
 * @component
 * @param {{ hotelIds: number[] }} props
 * @returns {JSX.Element|null} A vertical list of hotel info cards or null if no data.
 */
export const ActivityInformationHotelDisplay = (props: Props) => {
  const query = useActivityInformationHotelQuery([...props.hotelIds].filter((val) => val != undefined));

  if (query.isLoading) {
    return <BaseSkeletonStack />;
  }
  if (!query.data) {
    return <></>;
  }
  return (
    <BaseCard
      size={'xs'}
      height={'fit-content'}
      w={'100%'}
      header={
        <Flex p={2} alignItems='center' justifyContent='space-between'>
          <Heading size='md'>Activity Hotels</Heading>
          <FooterFlsqBadge flsq={props.flsq} />
        </Flex>
      }>
      <Flex flexDir={'column'} gap={'8px'} h={'fit-content'} w='100%'>
        {query.data.map((val, key) => (
          <HotelInfoCard val={val} key={key} hotelId={props.hotelIds[key]} />
        ))}
      </Flex>
    </BaseCard>
  );
};
